<script lang="ts" setup>
import { computed, ref, useTemplateRef, watch } from '#imports';
import { useService } from '@kaokei/use-vue-service';
import { useElementVisibility } from '@vueuse/core';
import HTImagePreview from '@core/components/HTImagePreview.vue';
import HTVideoPreview from '@core/components/HTVideoPreview.vue';
import type { AudioMsgVO, ImgMsgVO, MsgVO, VideoMsgVO } from '@core/types/im';
import { NEW_MSG_TYPE } from '../../constant';
import { ImService } from '../../im.service';
import {
  flashBackground,
  isAudioMsg,
  isCodeMsg,
  isImgMsg,
  isPhoneMsg,
  isTxtMsg,
  isVideoMsg,
  offscreenRunner,
} from '../../utils';
import ImMsgAudio from './ImMsgAudio.vue';
import ImMsgCode from './ImMsgCode.vue';
import ImMsgImg from './ImMsgImg.vue';
import ImMsgLoadMore from './ImMsgLoadMore.vue';
import ImMsgNotSupport from './ImMsgNotSupport.vue';
import ImMsgPhone from './ImMsgPhone.vue';
import ImMsgTxt from './ImMsgTxt.vue';
import ImMsgVideo from './ImMsgVideo.vue';

const service = useService(ImService);

const imMsgListRef = useTemplateRef<HTMLElement>('im-msg-list');
const listContainerRef = useTemplateRef<HTMLElement>('list-container');
const loadMoreRef = useTemplateRef<HTMLElement>('load-more');
const loadMoreVisible = useElementVisibility(loadMoreRef);

let isListEmpty = true;
let isBottom = true;
let lastMsg = {} as MsgVO;
let prevScrollTop = 0;
let prevScrollHeight = 0;

const previewImgVisible = ref(false);
const previewImgMsg = ref<ImgMsgVO>();

const previewVideoVisible = ref(false);
const previewVideoMsg = ref<VideoMsgVO>();

function handleRead(msg: MsgVO, readStatus?: number) {
  service.messageService.readMsg(msg, readStatus);
}

function handleRetry(msg: MsgVO) {
  service.messageService.resendMsg(msg);
}

function handleRetryImg(msg: ImgMsgVO) {
  service.messageService.resendImgMsg(msg);
}

function handlePreviewImg(msg: ImgMsgVO) {
  previewImgMsg.value = msg;
  previewImgVisible.value = true;
}

function handlePreviewVideo(msg: VideoMsgVO) {
  previewVideoMsg.value = msg;
  previewVideoVisible.value = true;
}

function handleRetryAudio(msg: AudioMsgVO) {
  service.messageService.resendAudioMsg(msg);
}

function scrollToBottomInstant() {
  scrollToBottom();
}
function scrollToBottomSmooth() {
  scrollToBottom('smooth');
}
function scrollToBottom(type: ScrollBehavior = 'instant') {
  const container = listContainerRef.value;
  if (container) {
    container.scrollTo({
      top: container.scrollHeight,
      behavior: type,
    });
  }
}
function scrollToFirstUnreadMsg() {
  const msg = service.messages.find(
    (item) => !item.isMine && item.readStatus === 0,
  );
  if (msg) {
    const container = listContainerRef.value;
    if (container) {
      const dom = container.querySelector(
        `.im-msg-item[data-id="${msg.msgId}"]`,
      );
      if (dom) {
        dom.scrollIntoView();
        const cdom = dom.querySelector<HTMLDivElement>('.im-msg-content');
        // 暂时先不处理背景闪烁的特效
        flashBackground(cdom);
      }
    }
  }
}

function scrollToFirstUnreadMsgByClick() {
  const msg = service.messages.find(
    (item) => !item.isMine && item.readStatus === 0,
  );
  if (msg) {
    scrollToFirstUnreadMsg();
  } else {
    // 如果前端没有找到未读消息，但是服务器端还是认为有未读消息
    // 需要通知服务器端全部已读
    service.messageService.readAllMsg();
  }
}

function updateScrollBeforeRender() {
  // lastMsg代表接收到的最新一条消息
  lastMsg = service.messages[service.messages.length - 1];
  const container = listContainerRef.value;
  if (container) {
    const offset =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    isBottom = offset < 20;
    prevScrollTop = container.scrollTop;
    prevScrollHeight = container.scrollHeight;
  }
}

function updateScrollAfterRender() {
  const container = listContainerRef.value;
  if (container) {
    const newMessageType = service.messageService.newMessageType;
    if (newMessageType === NEW_MSG_TYPE.APPEND_PEER) {
      // 这里是接收老师发送的消息
      if (isBottom) {
        if (service.entryVisible) {
          // 滚动到第一条未读消息
          // 针对phone消息，不需要增加未读消息数量
          service.increaseUnread(lastMsg);
          scrollToFirstUnreadMsg();
        } else {
          // 如果用户已经处于底部20px以内，则自动滚动到底部
          // 针对语音消息+图片消息+视频消息，也需要增加未读消息数量
          service.increaseUnreadIfNeedPlay(lastMsg);
          scrollToBottomSmooth();
        }
      } else {
        // 当前滚动条位置不在底部，不用处理，默认就是保持当前滚动位置不变
        // 只需要增加未读消息数量即可
        // 针对phone消息，不需要增加未读消息数量
        service.increaseUnread(lastMsg);
      }
    } else if (newMessageType === NEW_MSG_TYPE.APPEND_SELF) {
      // 这里是自己发送的消息，则自动滚动到底部
      scrollToBottomSmooth();
    } else if (newMessageType === NEW_MSG_TYPE.PREPEND) {
      if (isListEmpty) {
        isListEmpty = false;
        // 如果是第一次加载历史消息，直接滚动到底部
        scrollToBottomInstant();
        // 重新滚动到第一条未读消息【没有未读消息则保持在最底部不动】
        scrollToFirstUnreadMsg();
      } else {
        // 如果是加载历史消息，则保持当前滚动位置不变
        const newTop =
          container.scrollTop + container.scrollHeight - prevScrollHeight;
        container.scrollTo({
          top: newTop,
          behavior: 'instant',
        });
      }
    }
  }
}

function unreadMsgGetPopupContainer() {
  return imMsgListRef.value as HTMLElement;
}
const unreadTooltipOffset = {
  offset: [20, 10],
};
const unreadTooltip = computed(() => {
  return `${service.unreadCount}条新消息未读`;
});

watch(loadMoreVisible, (visible) => {
  if (visible) {
    if (service.messageService.hasNext) {
      service.messageService.loadMsgHistory();
    }
  }
});

watch(
  () => service.messages.length,
  () => {
    const chatBox = document.querySelector('.im-chat-box') as HTMLElement;
    offscreenRunner(chatBox, updateScrollBeforeRender);
  },
);

watch(
  () => service.messages.length,
  () => {
    const chatBox = document.querySelector('.im-chat-box') as HTMLElement;
    offscreenRunner(chatBox, updateScrollAfterRender);
  },
  { flush: 'post' },
);
</script>

<template>
  <div ref="im-msg-list" class="relative h-0 grow">
    <div
      ref="list-container"
      class="h-full w-full overflow-y-auto overscroll-contain bg-white px-6 py-4"
    >
      <div
        v-if="service.messages.length === 0"
        class="h-full w-full flex-center"
      >
        <a-empty description="暂无消息" />
      </div>
      <div
        v-else
        class="im-msg-wrapper"
        style="
          --color-a: rgba(0, 0, 0, 0.06);
          --color-b: rgba(22, 119, 225, 0.5);
        "
      >
        <ImMsgLoadMore ref="load-more" />
        <div
          v-for="item in service.messages"
          :key="item.msgId"
          class="im-msg-item mt-4"
          :data-id="item.msgId"
        >
          <ImMsgTxt
            v-if="isTxtMsg(item)"
            :msg="item"
            @retry="handleRetry(item)"
            @read="handleRead(item)"
          />
          <ImMsgImg
            v-else-if="isImgMsg(item)"
            :msg="item"
            @retry="handleRetryImg(item)"
            @read="handleRead(item)"
            @preview="handlePreviewImg(item)"
          />
          <ImMsgAudio
            v-else-if="isAudioMsg(item)"
            :msg="item"
            @retry="handleRetryAudio(item)"
            @read="handleRead(item, $event)"
          />
          <ImMsgVideo
            v-else-if="isVideoMsg(item)"
            :msg="item"
            @read="handleRead(item, $event)"
            @preview="handlePreviewVideo(item)"
          />
          <ImMsgCode
            v-else-if="isCodeMsg(item)"
            :msg="item"
            @retry="handleRetry(item)"
            @read="handleRead(item)"
          />
          <ImMsgPhone v-else-if="isPhoneMsg(item)" :msg="item" />
          <ImMsgNotSupport v-else :msg="item" @read="handleRead(item)" />
        </div>
      </div>
    </div>

    <HTImagePreview
      v-model:visible="previewImgVisible"
      :url="previewImgMsg?.msgBody.imgMsg.url"
    />

    <HTVideoPreview
      v-model:visible="previewVideoVisible"
      :poster="previewVideoMsg?.msgBody.videoMsg.backgroundImage"
      :url="previewVideoMsg?.msgBody.videoMsg.url"
    />

    <a-tooltip
      :open="service.unreadCount > 0"
      :get-popup-container="unreadMsgGetPopupContainer"
      :align="unreadTooltipOffset"
      :arrow="false"
    >
      <template #title>
        <div class="cursor-pointer" @click="scrollToFirstUnreadMsgByClick">
          {{ unreadTooltip }}
        </div>
      </template>
      <div class="pointer-events-none invisible absolute bottom-0 right-0">
        {{ unreadTooltip }}
      </div>
    </a-tooltip>
  </div>
</template>

<style scoped>
.im-msg-wrapper::before {
  content: '';
  display: block;
  pointer-events: none;
  position: absolute;
  z-index: 1;
  width: 100%;
  height: 40px;
  left: 0;
  top: 0;
  background: linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
}
</style>
